import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { UserService } from '../user/services/user.service';
import { ActionApiKey, ActionConfig, FieldConfig, FieldType } from '../../utils/dynamic-form/form.type';
import { Validators } from '@angular/forms';
import { KeyStatus } from '../user/models/user.utils';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { AuthService } from '../../services/guard/auth.service';
import { UserDetailComponent } from '../user/user-detail/user-detail.component';
@Component({
  selector: 'app-profile-tmp',
  standalone: true,
  imports: [
    ButtonModule,
    AvatarModule,
    TranslateModule,
    TagModule,
    ToastModule,
    CardModule,
    AvatarModule,
    UserDetailComponent
  ],
  templateUrl: './profile-tmp.component.html',
  styleUrl: './profile-tmp.component.scss',
  providers: [TranslateService, MessageService],
})
export class ProfileTmpComponent implements OnInit {
  fieldsEdit: FieldConfig[] = [];
  fieldsView: FieldConfig[] = [];
  user: any;
  editable: boolean = false;
  formNotTouch = true;
  buttonEnabled = false;
  messages = [];
  @ViewChild('userDetail') userDetail!: UserDetailComponent;
  
  constructor(
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly messageService: MessageService,
    private readonly authService: AuthService
  ) {}
  
  ngOnInit(): void {
    this.user = this.userService.getUser();
    this.translate.onLangChange.subscribe(() => {
      this.messages = [{ 
        severity: 'warn', 
        detail: this.translate.instant('messages.warningTestEnv') 
      }];
    });
    
    this.initializeFields();
  }

  private initializeFields(): void {
    // Initialisation de fieldsEdit
    this.fieldsEdit = [
      {
        name: 'firstname',
        label: 'user.firstname',
        value: this.user?.firstname,
        type: FieldType.input,
        visibleLabel: true,
        alphabeticOnly: true,
        validation: [Validators.required, Validators.pattern(/^\s*\S.*$/)],
      },
      {
        name: 'lastname',
        label: 'user.lastname',
        value: this.user?.lastname,
        type: FieldType.input,
        visibleLabel: true,
        alphabeticOnly: true,
        validation: [Validators.required, Validators.pattern(/^\s*\S.*$/)]
      },
      {
        name: 'defaultRole',
        label: 'user.defaultRole',
        value: this.user?.defaultRole?.roleName,
        type: FieldType.input,
        disabled: true,
        visibleLabel: true,
      },
      {
        name: 'email',
        label: 'user.email',
        value: this.user?.email,
        type: FieldType.input,
        disabled: true,
        visibleLabel: true,
      },
      {
        name: 'password',
        label: 'user.password',
        value: 'user.sendActivationLink',
        type: FieldType.button,
        subType: 'text',
        iconPrefix: 'pi pi-envelope',
        visibleLabel: true,
        visible: 'edit',
      },
      {
        name: 'defaultLanguage',
        label: 'user.defaultLanguage',
        value: this.user.defaultLanguage,
        type: FieldType.select,
        options: this.user?.relatedLanguages,
        optionLabel: 'languageName',
        visibleLabel: true,
      },
      {
        name: 'defaultShop',
        label: 'shop.shop',
        value: this.user.defaultShop,
        options: this.user.relatedShops,
        optionLabel: 'shopName',
        type: FieldType.select,
        visibleLabel: true,
        composite: true,
      },
    ];
    
    // Initialisation de fieldsView
    this.fieldsView = [
      {
        name: 'firstname',
        label: 'user.firstname',
        value: this.user?.firstname,
      },
      {
        name: 'lastname',
        label: 'user.lastname',
        value: this.user?.lastname,
      },
      {
        name: 'defaultRole',
        label: 'user.defaultRole',
        value: this.user?.defaultRole?.roleName,
        tooltip: this.user?.defaultRole?.roleDescription,
      },
      {
        name: 'email',
        label: 'user.email',
        value: this.user?.email,
      },
      {
        name: 'password',
        label: 'user.password',
        value: '***********',
      },
      {
        name: 'defaultLanguage',
        label: 'user.defaultLanguage',
        value: this.user?.defaultLanguage?.languageName,
      },
      {
        name: 'legalEntity',
        label: 'user.legalEntity',
        value: this.user?.legalEntityName,
      },
      {
        name: 'defaultShop',
        label: 'user.defaultShop',
        value: this.user?.defaultShop?.shopName + ' - ' + this.user.defaultShop?.shopChannel?.channelName,
      },
      {
        name: 'apiKey',
        label: 'user.apiKey',
        value: 'keyStatus.' + this.user?.keyStatus || 'keyStatus.' + KeyStatus.non_activee,
        customStyle: true,
        status: this.user?.keyStatus ?? KeyStatus.non_activee,
        actionButton: this.showActionButton(this.user?.keyStatus),
      },
    ];
  }

  showActionButton(status: string): ActionConfig {
    const actionConfig: ActionConfig = {
      isDisplayed: false,
    };
    
    if (status === KeyStatus.non_activee) {
      actionConfig.type = ActionApiKey.activate;
      actionConfig.isDisplayed = true;
    } else if (status === KeyStatus.activee) {
      actionConfig.type = ActionApiKey.regenerate;
      actionConfig.isDisplayed = true;
    }
    
    return actionConfig;
  }

  handleAction(event) {
    this.userService.resetPassword(this.user.email).subscribe({
      next: () => {
        this.updateForm();
      },
      error: () => {
        this.addMessage('error', 'messages.error', 'messages.errorSummary');
      },
    });
  }

  updateForm() {
    const user = {
      email: this.user?.email,
      firstname: this.user?.firstname,
      lastname: this.user?.lastname,
      shopCode: this.user.defaultShop?.shopCode,
      shopName: this.user.defaultShop?.shopName,
      channelCode: this.user.defaultChannel?.channelCode,
      defaultLanguage: this.user.defaultLanguage,
      isSeller: this.userService.isDefaultSeller(this.user),
      phoneNumber: this.user?.phoneNumber,
    };
    
    this.userService.updateUser(user).subscribe({
      next: () => {
        this.userService.setUser(this.user);
        this.addMessage('success', 'messages.success', 'messages.saveSuccess');
        this.reloadPage();
        this.editable = false;
      },
      error: () => {
        this.addMessage('error', 'messages.error', 'messages.errorSummary');
      },
    });
  }

  private addMessage(severity: string, summary: string, detail: string, life?: number): void {
    this.messageService.add({
      severity,
      summary: this.translate.instant(summary),
      detail: this.translate.instant(detail),
      life: life || undefined,
    });
  }

  reloadPage() {
    globalThis.location.reload();
  }

  detectFormState(event) {
    this.editable = !this.editable;
  }

  logout() {
    this.authService.logout();
  }
  
  changeBtnStatus(user) {
    this.user = user;
    this.formNotTouch = false;
  }
  
  formValid(valid: boolean) {
    this.buttonEnabled = valid;
  }
}
